/**
 * Dashboard Page - 관리자 대시보드 (프로토타입)
 * 더미 데이터 사용
 */

import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  HiUsers, 
  HiLocationMarker, 
  HiBell, 
  HiChartBar,
  HiLogout,
  HiSparkles,
  HiTrendingUp,
  HiTrendingDown,
  HiClock,
  HiViewGrid,
  HiMap
} from 'react-icons/hi';
import { format } from 'date-fns';
import { mockDashboardData } from '../data/mockData';

export default function Dashboard() {
  const navigate = useNavigate();
  const stats = mockDashboardData.overview;
  const topSpots = mockDashboardData.top_spots;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      {/* Header */}
      <motion.header
        className="relative overflow-hidden shadow-2xl border-b-2"
        style={{
          background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`,
          borderColor: 'var(--color-primary)',
        }}
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-[95%] mx-auto px-2 sm:px-4 lg:px-6 py-5">
          <div className="flex justify-between items-center">
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="flex items-center gap-4">
                <motion.div
                  className="p-2.5 bg-white/20 backdrop-blur-md rounded-2xl shadow-lg border border-white/30"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <HiSparkles className="w-8 h-8 text-white" />
                </motion.div>
                <div>
                  <h1 className="text-2xl font-display font-extrabold text-white tracking-tight drop-shadow-lg">
                    팔팔구구 <span className="bg-gradient-to-r from-yellow-200 to-pink-200 bg-clip-text text-transparent">메타보맵</span>
                  </h1>
                  <p className="text-sm font-medium text-white/90 mt-0.5">관리자 대시보드</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="flex items-center space-x-4"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="text-right">
                <p className="text-sm font-bold text-white drop-shadow-md">Admin</p>
                <p className="text-xs font-medium text-white/80">관리자</p>
              </div>
              <motion.button
                onClick={() => navigate('/login')}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-white/20 backdrop-blur-md border border-white/30 rounded-xl hover:bg-white/30 flex items-center gap-2 shadow-lg transition-all"
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.95 }}
              >
                <HiLogout className="w-4 h-4" />
                로그아웃
              </motion.button>
            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-[95%] mx-auto px-2 sm:px-4 lg:px-6 py-8">
        {/* Navigation Tabs */}
        <motion.div
          className="mb-8 rounded-2xl shadow-lg border-2 p-2"
          style={{
            background: 'var(--color-surface)',
            borderColor: 'var(--color-primary)',
          }}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <nav className="flex space-x-2">
            <motion.button
              onClick={() => navigate('/dashboard')}
              className="py-3 px-6 rounded-xl font-semibold text-sm relative flex items-center gap-2 transition-all duration-200"
              style={{
                background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`,
                color: 'white',
              }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiViewGrid className="w-5 h-5" />
              대시보드
            </motion.button>
            <motion.button
              onClick={() => navigate('/spots')}
              className="py-3 px-6 rounded-xl font-semibold text-sm relative flex items-center gap-2 transition-all duration-200"
              style={{
                color: 'var(--color-text)',
              }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiMap className="w-5 h-5" />
              스팟 관리
            </motion.button>
          </nav>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {/* Total Users */}
          <motion.div
            className="rounded-xl shadow-lg p-6 border-2"
            style={{
              background: 'var(--color-surface)',
              borderColor: 'var(--color-primary)',
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ y: -8, scale: 1.02 }}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3.5 rounded-xl shadow-md" style={{ background: 'var(--color-primary)' }}>
                  <HiUsers className="w-7 h-7 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>총 사용자</p>
                <p className="text-3xl font-extrabold gradient-text">{stats.total_users || 0}</p>
              </div>
            </div>
          </motion.div>

          {/* Total Spots */}
          <motion.div
            className="rounded-xl shadow-lg p-6 border-2"
            style={{
              background: 'var(--color-surface)',
              borderColor: 'var(--color-secondary)',
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ y: -8, scale: 1.02 }}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3.5 rounded-xl shadow-md" style={{ background: 'var(--color-secondary)' }}>
                  <HiLocationMarker className="w-7 h-7 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>활성 스팟</p>
                <p className="text-3xl font-extrabold gradient-text">{stats.total_active_spots || 0}</p>
              </div>
            </div>
          </motion.div>

          {/* Today's Notifications */}
          <motion.div
            className="rounded-xl shadow-lg p-6 border-2"
            style={{
              background: 'var(--color-surface)',
              borderColor: 'var(--color-accent)',
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ y: -8, scale: 1.02 }}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3.5 rounded-xl shadow-md" style={{ background: 'var(--color-accent)' }}>
                  <HiBell className="w-7 h-7 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>오늘 알림</p>
                <p className="text-3xl font-extrabold gradient-text">{stats.total_notifications_today || 0}</p>
              </div>
            </div>
          </motion.div>

          {/* Monthly Visits */}
          <motion.div
            className="rounded-xl shadow-lg p-6 border-2"
            style={{
              background: 'var(--color-surface)',
              borderColor: 'var(--color-primary)',
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            whileHover={{ y: -8, scale: 1.02 }}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3.5 rounded-xl shadow-md" style={{ background: 'var(--color-primary)' }}>
                  <HiChartBar className="w-7 h-7 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>이번 달 방문</p>
                <p className="text-3xl font-extrabold gradient-text">{stats.total_visits_this_month || 0}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Top Spots Table */}
        <motion.div
          className="rounded-xl shadow-lg border-2"
          style={{
            background: 'var(--color-surface)',
            borderColor: 'var(--color-primary)',
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div className="px-6 py-5 border-b-2" style={{ borderColor: 'var(--color-primary)' }}>
            <h3 className="text-xl font-display font-bold" style={{ color: 'var(--color-text)' }}>인기 스팟 Top 5</h3>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>최근 30일 기준</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y-2" style={{ borderColor: 'var(--color-primary)' }}>
              <thead style={{ background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))` }}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">순위</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">스팟명</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">총 방문 수</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">고유 방문자</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">평균 측정 (nT)</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">최고 측정 (nT)</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">최저 측정 (nT)</th>
                </tr>
              </thead>
              <tbody className="divide-y-2" style={{ borderColor: 'var(--color-primary)' }}>
                {topSpots.map((spot, index) => (
                  <motion.tr 
                    key={spot.id}
                    className="transition-all duration-150 hover:opacity-80"
                    style={{
                      background: index % 2 === 0 ? 'var(--color-surface)' : 'var(--color-background)',
                    }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center justify-center w-10 h-10 rounded-2xl shadow-lg font-bold text-lg text-white ${
                        index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' :
                        index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500' :
                        index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600' :
                        'bg-gradient-to-br from-indigo-400 to-indigo-600'
                      }`}>
                        {index + 1}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>{spot.spot_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm" style={{ color: 'var(--color-text)' }}>{spot.visit_count || 0}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm" style={{ color: 'var(--color-text)' }}>{spot.unique_visitors || 0}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm" style={{ color: 'var(--color-text)' }}>
                        <HiChartBar className="w-4 h-4 mr-1" style={{ color: 'var(--color-primary)' }} />
                        {spot.avg_measurement ? parseFloat(spot.avg_measurement).toFixed(2) : '0.00'} nT
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm" style={{ color: 'var(--color-text)' }}>
                        <div className="flex items-center">
                          <HiTrendingUp className="w-4 h-4 mr-1 text-green-500" />
                          {spot.max_measurement ? parseFloat(spot.max_measurement).toFixed(2) : '0.00'} nT
                        </div>
                        {spot.max_measurement_timestamp && (
                          <div className="flex items-center text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                            <HiClock className="w-3 h-3 mr-1" />
                            {format(new Date(spot.max_measurement_timestamp), 'yy.MM.dd HH:mm')}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm" style={{ color: 'var(--color-text)' }}>
                        <div className="flex items-center">
                          <HiTrendingDown className="w-4 h-4 mr-1 text-red-500" />
                          {spot.min_measurement ? parseFloat(spot.min_measurement).toFixed(2) : '0.00'} nT
                        </div>
                        {spot.min_measurement_timestamp && (
                          <div className="flex items-center text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                            <HiClock className="w-3 h-3 mr-1" />
                            {format(new Date(spot.min_measurement_timestamp), 'yy.MM.dd HH:mm')}
                          </div>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
